import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { articlesAPI } from '../../lib/api';
import { AlertTriangle } from 'lucide-react';
import Marquee from 'react-fast-marquee';
import { useI18n } from '../../lib/i18n';
import { cleanArticleTitle } from '../../lib/utils';

const BREAKING_INITIAL_DELAY_MS = 150;
const BREAKING_REFRESH_INTERVAL_MS = 60000;
const BREAKING_CLOCK_INTERVAL_MS = 15000;

function localeFromLanguage(language) {
  return 'fr-FR';
}

export default function BreakingNews() {
  const { language, t } = useI18n();
  const [items, setItems] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [clock, setClock] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;

    const loadItems = async () => {
      try {
        const breakingRes = await articlesAPI.breaking();
        let merged = breakingRes.data || [];
        if (!merged.length) {
          const latestRes = await articlesAPI.latest(8);
          merged = latestRes.data || [];
        }
        if (cancelled) return;

        const seen = new Set();
        const nextItems = merged
          .filter((item) => {
            const key = item?.id || item?.slug || item?.title;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((a, b) => new Date(b?.published_at || 0).getTime() - new Date(a?.published_at || 0).getTime())
          .slice(0, 8);

        setItems(nextItems);
        setLastUpdatedAt(Date.now());
      } catch (_) {}
    };

    const initialTimerId = window.setTimeout(loadItems, BREAKING_INITIAL_DELAY_MS);
    const refreshId = window.setInterval(loadItems, BREAKING_REFRESH_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadItems();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimerId);
      window.clearInterval(refreshId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [language]);

  useEffect(() => {
    const timerId = window.setInterval(() => setClock(Date.now()), BREAKING_CLOCK_INTERVAL_MS);
    return () => window.clearInterval(timerId);
  }, []);

  const updatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return '';
    const rtf = new Intl.RelativeTimeFormat(localeFromLanguage(language), { numeric: 'auto' });
    const diffInSeconds = Math.round((lastUpdatedAt - clock) / 1000);

    if (Math.abs(diffInSeconds) < 60) {
      return `${t('home.updated_label', 'Atualizado')} ${rtf.format(diffInSeconds, 'second')}`;
    }

    const diffInMinutes = Math.round(diffInSeconds / 60);
    if (Math.abs(diffInMinutes) < 60) {
      return `${t('home.updated_label', 'Atualizado')} ${rtf.format(diffInMinutes, 'minute')}`;
    }

    const formattedTime = new Date(lastUpdatedAt).toLocaleTimeString(localeFromLanguage(language), {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${t('home.updated_label', 'Atualizado')} ${formattedTime}`;
  }, [clock, language, lastUpdatedAt, t]);

  if (!items.length) return null;

  return (
    <div className="bg-[#c4170c] text-white overflow-hidden" data-testid="breaking-news-bar">
      <div className="flex items-center">
        <div className="bg-[#a11008] px-4 py-2 flex items-center gap-2 shrink-0 z-10">
          <AlertTriangle size={14} />
          <span className="text-xs font-bold uppercase tracking-[0.15em]">{t('home.breaking_label', 'Alerte Info')}</span>
        </div>
        <div className="min-w-0 flex-1">
          <Marquee speed={40} gradient={false} className="py-2">
            {items.map((item, i) => (
              <Link
                key={item.id || i}
                to={`/article/${item.slug || item.id}`}
                className="text-sm font-medium mx-8 hover:underline whitespace-nowrap"
                data-testid={`breaking-item-${i}`}
              >
                {cleanArticleTitle(item.title, item)}
              </Link>
            ))}
          </Marquee>
        </div>
        {updatedLabel && (
          <span className="shrink-0 px-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-white/80 sm:px-4 sm:text-[10px]">
            {updatedLabel}
          </span>
        )}
      </div>
    </div>
  );
}
